import { useState, useEffect, useRef } from "react";
import QuestionLayout from "./QuestionLayout";
import AudioPlayButton from "./AudioPlayButton";
import { renderStaffLines, renderClef, renderTimeSignature } from "./staff/staffRender";
import { LINE_GAP } from "./staff/pitchMath";
import { Glyph } from "./staff/glyphs";
import SymbolPalette from "./staff/SymbolPalette";

/* ============================================================
   CONSTANTS
   ============================================================ */

const GLYPH_WIDTH = {
  whole_note: 34,
  half_note: 28,
  dotted_half_note: 34,
  quarter_note: 26,
  eighth_note: 24,
  sixteenth_note: 24,

  eighth_pair: 38,
  eighth_four: 72,

  sixteenth_sixteenth_eighth: 52,
  eighth_sixteenth_sixteenth: 52,
  sixteenth_four: 64,

  "1e1e1e": 72,
  "2s1e1e": 82,
  "1e2s1e": 82,
  "1e1e2s": 82,
  "2s2s1e": 92,
  "2s1e2s": 92,
  "1e2s2s": 92,
  "2s2s2s": 102,

  quarter_rest: 26,
  half_rest: 26,
  whole_rest: 30
};

const DEFAULT_GLYPH_WIDTH = 26;
const GLYPH_GAP = 10;

const STAFF_TOP = 40;
const LEFT_MARGIN = 110;

const MEASURE_MIN_WIDTH = 90;      // ⭐ NEW
const MEASURE_PADDING = 24;        // ⭐ NEW

/* ============================================================
   WIDGET
   ============================================================ */

export default function RhythmEditorWidget({
  config,
  value = {},
  onChange,
  mode = "edit",
  feedback
}) {
  const containerRef = useRef(null);
  const disabled = mode !== "edit";
  const [currentMeasure, setCurrentMeasure] = useState(1);

  const audioValue = value._audio || {};
  const allowedSymbols = config.allowedSymbols;

  /* ───────── NORMALIZE VALUE ───────── */

  const rhythm = value.rhythm || {
    measures: Array.from({ length: config.measures }, () => ({ rhythms: [] }))
  };

  function isGivenMeasure(mi) {
    return Array.isArray(config.givenRhythms?.[mi]);
  }

  function measureContentWidth(symbols = []) {
    let w = 0;
    symbols.forEach((sym, i) => {
      const gw = GLYPH_WIDTH[sym] ?? DEFAULT_GLYPH_WIDTH;
      w += gw;
      if (i > 0) w += GLYPH_GAP;
    });
    return w;
  }

  /* ───────── UPDATE ───────── */

  function update(nextRhythm) {
    if (!onChange || disabled) return;
    onChange({ ...value, rhythm: nextRhythm });
  }

  function addRhythm(symbol) {
    if (isGivenMeasure(currentMeasure)) return;
    const next = structuredClone(rhythm);
    next.measures[currentMeasure].rhythms.push(symbol);
    update(next);
  }

  function deleteLast() {
    if (isGivenMeasure(currentMeasure)) return;
    const next = structuredClone(rhythm);
    next.measures[currentMeasure].rhythms.pop();
    update(next);
  }

  /* ───────── KEYBOARD ───────── */

  useEffect(() => {
    if (disabled) return;

    const el = containerRef.current;
    if (!el) return;

    function onKeyDown(e) {
        if (e.key === "Backspace") {
        e.preventDefault();
        deleteLast();
        }
    }

    el.addEventListener("keydown", onKeyDown);
    return () => el.removeEventListener("keydown", onKeyDown);
    }, [disabled, rhythm, currentMeasure]);


  /* ───────── AUDIO STATE ───────── */

  function setAudioState(audioState) {
    if (disabled) return;
    onChange({ ...value, _audio: audioState });
  }

  /* ───────── GEOMETRY (⭐ FIXED) ───────── */

  const measureSymbols = rhythm.measures.map((m, i) =>
    isGivenMeasure(i) ? config.givenRhythms[i] : m.rhythms
  );

  const measureWidths = measureSymbols.map(symbols => {
    const content = measureContentWidth(symbols);
    return Math.max(MEASURE_MIN_WIDTH, content + MEASURE_PADDING * 2);
  });

  const staffWidth =
    LEFT_MARGIN + measureWidths.reduce((a, b) => a + b, 0);

  const STAFF_HEIGHT = LINE_GAP * 4;
  const MIDDLE_LINE_Y = STAFF_TOP + 3.5 * LINE_GAP;

  /* ───────── RENDER ───────── */

  return (
    <QuestionLayout
      number={config.number}
      prompt={config.prompt}
      points={config.points}
      mode={mode}
      feedback={feedback}
    >
        <div
        ref={containerRef}
        tabIndex={0}
        style={{ outline: "none" }}
        onClick={() => containerRef.current?.focus()}
        >
      {/* AUDIO */}
      {config.audio && (
        <div style={{ marginBottom: 12 }}>
          <AudioPlayButton
            src={config.audio}
            value={audioValue}
            onChange={setAudioState}
            mode={mode}
            maxListens={config.maxListens ?? 1}
          />
        </div>
      )}

      {/* PALETTE */}
      <SymbolPalette
        allowedSymbols={allowedSymbols}
        selected={null}
        onSelect={addRhythm}
        disabled={disabled}
      />

      {/* STAFF */}
      <svg
        width={staffWidth + 50}
        height={STAFF_TOP + STAFF_HEIGHT + 40}
        style={{ border: "1px solid #ccc", borderRadius: 4 }}
      >
        {renderStaffLines(staffWidth, STAFF_TOP)}
        {renderClef("treble")}
        {renderTimeSignature(config.timeSig)}

        {rhythm.measures.map((measure, mi) => {
          const mx =
            LEFT_MARGIN +
            measureWidths.slice(0, mi).reduce((a, b) => a + b, 0); // ⭐ FIXED

          const mw = measureWidths[mi];
          const partFeedback = feedback?.parts?.[mi];

          return (
            <g key={mi}>
              {/* BARLINE */}
              {mi > 0 && (
                <rect
                  x={mx}
                  y={STAFF_TOP}
                  width={1}
                  height={STAFF_HEIGHT}
                  fill="#000"
                />
              )}

              {/* CLICK AREA */}
              <rect
                x={mx}
                y={STAFF_TOP - 20}
                width={mw}
                height={STAFF_HEIGHT + 40}
                fill={
                isGivenMeasure(mi)
                    ? "transparent"
                    : mode === "review"
                    ? partFeedback?.correct
                    ? "rgba(46,125,50,0.08)"
                    : "rgba(198,40,40,0.08)"
                    : mi === currentMeasure
                    ? "rgba(0,0,255,0.05)"
                    : "transparent"
                }
                onClick={() => {
                  if (isGivenMeasure(mi)) return;
                  if (!disabled) setCurrentMeasure(mi);
                }}
              />

              {/* RHYTHM GLYPHS */}
              {(() => {
                const symbols = measureSymbols[mi];
                let cursorX = mx + MEASURE_PADDING;

                return symbols.map((symbol, i) => {
                  const width = GLYPH_WIDTH[symbol] ?? DEFAULT_GLYPH_WIDTH;
                  const node = (
                    <g
                      key={i}
                      transform={`translate(${cursorX}, ${MIDDLE_LINE_Y})`}
                    >
                      <Glyph symbol={symbol} />
                    </g>
                  );
                  cursorX += width + GLYPH_GAP;
                  return node;
                });
              })()}
            </g>
          );
        })}

        {/* END BARLINE */}
        <rect
          x={staffWidth - 6}
          y={STAFF_TOP}
          width={1}
          height={40}
          fill="#000"
        />
        <rect
          x={staffWidth - 3}
          y={STAFF_TOP}
          width={3}
          height={40}
          fill="#000"
        />
      </svg>

      {/* DELETE */}
      {!disabled && (
        <div style={{ marginTop: 8 }}>
          <button onClick={deleteLast}>Delete</button>
        </div>
      )}

      </div>
    </QuestionLayout>
  );
}
